import type { ConversationState, ConversationView } from '@hilo/contracts';

/** Acción del encabezado o de reasignación; se guarda para poder repetirla con la revisión vigente. */
export type PaneAction =
  | { kind: 'claim'; label: string }
  | { kind: 'state'; to: ConversationState; label: string }
  | { kind: 'assign'; assigneeId: string | null; label: string };

/** Lo que cambian las acciones. No se compara la revisión: también sube con cada mensaje. */
export type ActionTarget = Pick<ConversationView, 'state' | 'assigneeId'>;

/**
 * Fallo de una acción. `unanswered`: Hilo no respondió (reinicio, red, 502-504), así que la acción pudo
 * aplicarse aunque no llegara la respuesta. `before` es la vista en la que se pulsó y `at`, el instante
 * del fallo: solo una vista recibida después sirve para saber qué pasó.
 */
export interface ActionFailure { action: PaneAction; text: string; unanswered: boolean; before: ActionTarget; at: number }

export interface ActionNotice { text: string; retry: boolean }

/** La vista ya muestra lo que se pidió, lo aplicara esta petición u otra persona. */
export function fulfils(view: ActionTarget, action: PaneAction, self: string): boolean {
  if (action.kind === 'claim') return view.assigneeId === self;
  if (action.kind === 'state') return view.state === action.to;
  return view.assigneeId === action.assigneeId;
}

/**
 * Aviso de un fallo según la vista vigente; `fresh` indica que esa vista llegó del servidor después del fallo.
 * - Si ya muestra lo pedido, no hay aviso: pudo aplicarse aunque se perdiera la respuesta.
 * - Sin respuesta ni vista nueva no se afirma que fallara; se comprueba en cuanto Hilo vuelva.
 * - Si la vista nueva sigue como al pulsar, la acción no se aplicó y se puede repetir.
 * - Si cambió de otro modo, se explica sin ofrecer repetir: valen las acciones de la vista actual.
 */
export function actionNotice(failure: ActionFailure, view: ActionTarget, fresh: boolean, self: string): ActionNotice | null {
  if (fresh && fulfils(view, failure.action, self)) return null;
  if (!failure.unanswered) return { text: failure.text, retry: false };
  const label = failure.action.label;
  if (!fresh) {
    return { text: `Hilo no respondió (puede estar reiniciándose) y aún no se sabe si se pudo ${label}. Se comprobará en cuanto vuelva.`, retry: false };
  }
  if (view.state === failure.before.state && view.assigneeId === failure.before.assigneeId) {
    return { text: `Hilo ya responde de nuevo, pero no se pudo ${label}.`, retry: true };
  }
  return { text: 'Hilo ya responde de nuevo. La conversación cambió mientras tanto: revisa cómo está ahora.', retry: false };
}
